export const freePlan = {
  id: 'free',
  name: 'Free',
  currency: 'eur',
  amount: 0,
  annualAmount: 0,
  features: [
    { name: '5 Posts / month' },
    { name: '1 Brand' },
    { name: '1 Moodboard' },
    { name: '100 Chat Messages / month' },
    { name: '25 Image Generations / month' },
  ],
} as const

export const standardPlan = {
  id: 'standard',
  name: 'Standard',
  currency: 'eur',
  amount: 29.99,
  annualAmount: 299.88,
  features: [
    { name: 'Unlimited Posts' },
    { name: '3 Brands' },
    { name: 'Unlimited Moodboards' },
    { name: '1,000 Chat Messages / month' },
    { name: '500 Image Generations / month' },
    { name: '10 Automations' },
    { name: 'MCP Access' },
  ],
} as const

export const proPlan = {
  id: 'pro',
  name: 'Pro',
  currency: 'eur',
  amount: 99.99,
  annualAmount: 995.88,
  features: [
    { name: 'Unlimited Posts' },
    { name: '10 Brands' },
    { name: 'Unlimited Moodboards' },
    { name: 'Unlimited Chat Messages' },
    { name: '2,000 Image Generations / month' },
    { name: '50 Automations' },
    { name: 'API + MCP Access' },
    { name: 'Priority Support' },
  ],
} as const

/** How long a claimed trial runs. The sidebar CTA copy mirrors this. */
export const TRIAL_DURATION_DAYS = 14

export const proTrialPlan = {
  id: 'pro-trial',
  name: 'Pro (Trial)',
  currency: 'eur',
  amount: 0,
  annualAmount: 0,
  features: proPlan.features,
} as const

export type BillingInterval = 'month' | 'year'

// AppSumo lifetime-deal tiers. Paid once on AppSumo, never billed by us —
// amount stays 0 so no billing surface ever tries to charge for them.
// Entitlements are granted via the AppSumo licensing webhook/redemption flow
// with `user_subscription.source = 'appsumo'` and `expiresAt = null`.
export const appsumoTier1Plan = {
  id: 'appsumo-tier-1',
  name: 'AppSumo Tier 1',
  currency: 'usd',
  amount: 0,
  annualAmount: 0,
  features: [
    { name: 'Lifetime access' },
    { name: '1 Brand' },
    { name: '25 Posts / month' },
    { name: '300 Image Generations / month' },
    { name: '500 Chat Messages / month' },
  ],
} as const

export const appsumoTier2Plan = {
  id: 'appsumo-tier-2',
  name: 'AppSumo Tier 2',
  currency: 'usd',
  amount: 0,
  annualAmount: 0,
  features: [
    { name: 'Lifetime access' },
    { name: '3 Brands' },
    { name: '100 Posts / month' },
    { name: '1,500 Image Generations / month' },
    { name: '5,000 Chat Messages / month' },
  ],
} as const

export const appsumoTier3Plan = {
  id: 'appsumo-tier-3',
  name: 'AppSumo Tier 3',
  currency: 'usd',
  amount: 0,
  annualAmount: 0,
  features: [
    { name: 'Lifetime access' },
    { name: 'Unlimited Brands' },
    { name: '300 Posts / month' },
    { name: 'Unlimited Image Generations' },
    { name: '20,000 Chat Messages / month' },
    { name: 'API Access' },
  ],
} as const

/** Map AppSumo's integer tier (from webhook payloads) to our plan id. */
export const appsumoTierToPlan = (tier: number): AppsumoPlanType => {
  if (tier <= 1) return 'appsumo-tier-1'
  if (tier === 2) return 'appsumo-tier-2'
  return 'appsumo-tier-3'
}

export const enterprisePlan = {
  id: 'enterprise',
  name: 'Enterprise',
  currency: 'eur',
  amount: 0,
  annualAmount: 0,
  features: [
    { name: 'Unlimited Everything' },
    { name: 'VIP Support' },
    { name: 'Custom Interface' },
    { name: 'Custom Integrations' },
  ],
} as const

const allPlans = [
  freePlan,
  standardPlan,
  proPlan,
  enterprisePlan,
  proTrialPlan,
  appsumoTier1Plan,
  appsumoTier2Plan,
  appsumoTier3Plan,
] as const
const purchasablePlans = [standardPlan, proPlan] as const
const appsumoPlans = [appsumoTier1Plan, appsumoTier2Plan, appsumoTier3Plan] as const

export const planTypes = allPlans.map((p) => p.id)
export const purchasablePlanTypes = purchasablePlans.map((p) => p.id)
export const appsumoPlanTypes = appsumoPlans.map((p) => p.id)
export type AppsumoPlanType = (typeof appsumoPlanTypes)[number]

export type PlanType = (typeof planTypes)[number]
export type PurchasablePlanType = (typeof purchasablePlanTypes)[number]

export type PricingPlan =
  | typeof freePlan
  | typeof proTrialPlan
  | typeof standardPlan
  | typeof proPlan
  | typeof enterprisePlan
  | typeof appsumoTier1Plan
  | typeof appsumoTier2Plan
  | typeof appsumoTier3Plan
export type PurchasablePlan = typeof standardPlan | typeof proPlan

export const plans: Record<string, PricingPlan> = {
  free: freePlan,
  'pro-trial': proTrialPlan,
  standard: standardPlan,
  pro: proPlan,
  enterprise: enterprisePlan,
  'appsumo-tier-1': appsumoTier1Plan,
  'appsumo-tier-2': appsumoTier2Plan,
  'appsumo-tier-3': appsumoTier3Plan,
}
